import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import Card from "../../components/card";
import TemplateDefault from "../../components/template-default";
import Button from "../../components/button";
import {FaFileExcel, FaPen, FaPlus, FaRegEye, FaRegEyeSlash} from "react-icons/fa";
import {useNavigate} from "react-router-dom";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import InputForm from "../../components/input-form";
import requestService from "../../services/request-service";
import general from "../../utils/general";
import useFetchData from "./hooks/use-fetch-data";
import SelectComponent from "../../components/select-component";
import statusCellRenderer from "./components/status-cell-renderer";
import cellCenter from "../../components/cell-center";
import MultiSelect from "./components/multi-select";
import {saveData} from "./hooks/save-data";
import {statusList} from "./resources/status-list";
import {FaEllipsis} from "react-icons/fa6";
import InputCheckbox from "../../components/input-checkbox";
import {ProjectContext} from "../../context/project-context";
import {buildAccentInsensitiveColDef, normalizeQuickFilterText, setAccentInsensitiveQuickFilter} from "../../utils/ag-grid-quick-filter";

const BASENAME = process.env.PUBLIC_URL;

let initialFilters = {
    project: "",
    dev: [],
    group: [],
    roadmap: false,
    status: [],
    responsible: "",
}

const Requests = () => {
    const navigate = useNavigate();
    const [filters, setFilters] = useState(initialFilters);
    const [counters, setCounters] = useState(null)
    const [showTableProperties, setShowTableProperties] = useState(false)
    const {projects, devs, groups, responsibles} = useFetchData();
    const {user} = useContext(ProjectContext);
    const onlyMyRequests = user.permissions.includes("request_only_my_user");

    useEffect(() => {
        if (projects) {
            addParamsToColumns("projects", projects);
        }
        if (devs) {
            addParamsToColumns("devs", devs);
        }
        if (groups) {
            addParamsToColumns("groups", groups);
        }
        if (responsibles) {
            addParamsToColumns("responsible", responsibles);
        }
    }, [projects, devs, groups, responsibles]);

    const addParamsToColumns = (field, values) => {
        setColumnDefs((prevColumnDefs) =>
            prevColumnDefs.map((colDef) => {
                if (colDef.field === field) {
                    return {
                        ...colDef,
                        cellRendererParams: {
                            ...colDef.cellRendererParams,
                            selectOptions : values
                        }
                    };
                }
                return colDef;
            })
        );
    }
    const handleSelect = (name, value) => setFilters(prev => ({...prev, [name]: value}));
    const handleRoadmap = (e) => setFilters(prev => ({...prev, 'roadmap': e.target.checked}));

    const queryClient = useQueryClient();

    const {data: requests} = useQuery({
        queryFn: () => requestService.list(filters).then(({data}) => data),
        queryKey: ['requests', {filters}]
    });

    useEffect(() => {
        if (requests) {
            countData(requests)
        }
    }, [requests]);

    const countData = (data) => {
        const counters = data.reduce((acc, {status}) => {
            acc[status?.toLowerCase()] = (acc[status?.toLowerCase()] || 0) + 1;
            return acc;
        }, {pendente: 0, negado: 0, backlog: 0, doing: 0, concluido: 0, travado: 0});
        counters.total = counters.pendente + counters.negado + counters.backlog + counters.doing + counters.concluido + counters.travado;
        setCounters(counters);
    }

    const getEditorParams = (params) => {
        const currentStatus = statusList.find(s => s.value === params.value);
        return {
            values: statusList.map(s => s.value),
            value: currentStatus ? currentStatus.value : "",
        }
    }
    const compareBytName = (valueA, valueB) => {
        const getName = (value) => {
            if (Array.isArray(value) && value.length > 0) return value[0].name.toLowerCase();
            if (value?.name) return value.name.toLowerCase();
            return "";
        };

        return getName(valueA).localeCompare(getName(valueB));
    };

    useEffect(() => {
        document.title = "SISTEMA INTERNO";

        const columns = [
            { field: "id", headerName: "ID", width: 40 },
            {field: "exec_order", headerName: "Ordem", width: 75, cellRenderer: cellCenter, sort: "asc", editable: true},
            {
                field: "title",
                headerName: "Título",
                flex: 1,
                minWidth: 200,
                wrapText: true,
                autoHeight: true,
                cellEditor: 'agTextCellEditor', editable: true,
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return general.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                field: "groups", headerName: "Grupos", flex: 1,
                getQuickFilterText: ({data: {groups}}) => groups.map(g => g.name).join(", "),
                wrapText: true, autoHeight: true,
                cellRenderer: MultiSelect,
                cellRendererParams: {
                    target: "groups"
                },
                comparator: compareBytName
            },
            {
                field: "projects", headerName: "Projetos", flex: 1,
                wrapText: true, autoHeight: true,
                getQuickFilterText: ({data: {projects}}) => projects.map(p => p.name).join(", "),
                cellRenderer: MultiSelect,
                cellRendererParams: {
                    target: "projects"
                },
                comparator: compareBytName
            },
            {
                field: "devs", headerName: "Devs", flex: 1,
                wrapText: true, autoHeight: true,
                getQuickFilterText: ({data: {devs}}) => devs.map(d => d.name).join(", "),
                cellRenderer: MultiSelect,
                cellRendererParams: {
                    target: "devs"
                },
                comparator: compareBytName
            },
            {
                field: "status", headerName: "Status", width: 100, cellRenderer: statusCellRenderer,
                cellEditor: 'agSelectCellEditor', editable: !onlyMyRequests,
                cellEditorParams: getEditorParams,
            },
            {
                sort: "asc",
                field: "date",
                headerName: "Solicitação",
                width: 120,
                cellRenderer: ({value}) => general.formatDate(value, "DD/MM/YYYY"),
                cellEditor: "agDateCellEditor",
                valueGetter: (params) => {
                    return params.data.date ? new Date(params.data.date) : null;
                },
                valueSetter: (params) => {
                    if (params.newValue) {
                        params.data.date = params.newValue.toISOString();
                        return true;
                    }
                    return false;
                }, editable: true
            },
            {
                field: "deadline",
                headerName: "Prazo de entrega",
                width: 130,
                cellRenderer: ({value}) => general.formatDate(value, "DD/MM/YYYY"),
                cellEditor: "agDateCellEditor",
                valueGetter: (params) => {
                    return params.data.deadline ? new Date(params.data.deadline) : null;
                },
                valueSetter: (params) => {
                    if (params.newValue) {
                        params.data.deadline = params.newValue.toISOString();
                        return true;
                    }
                    return false;
                }, editable: true
            },
            {field: "gitlab", headerName: "GitLab", sort: "asc", editable: true, hide: true},
            {field: "requester", headerName: "Solicitante", sort: "asc", editable: true, hide: true},
            {
                field: "responsible", headerName: "Atendente", flex: 1,
                wrapText: true, autoHeight: true,
                getQuickFilterText: ({data: {responsible}}) => normalizeQuickFilterText(responsible?.name ?? ""),
                cellRenderer: MultiSelect,
                cellRendererParams: {
                    target: "responsible",
                    selectionMode: "single"
                },
                comparator: compareBytName
            },
            {field: "", headerName:"", sortable: false, width: 40, editable: false,
                cellRenderer: (params) => {
                    return (<span onClick={() => edit(params)}
                                  className="w-[25px] h-[25px] m-auto flex justify-center items-center text-white bg-blue-700 cursor-pointer rounded-md">
                            <FaPen size={"12px"}/>
                        </span>)
                }},
        ];

        const edit = ({data: {id}}) => {
            window.open(`${BASENAME}/requests/form/${id}`);
        }

        const hiddenColumns = JSON.parse(localStorage.getItem("request-hidden-columns"));
        const orderColumns = JSON.parse(localStorage.getItem("request-order-columns"));

        if (hiddenColumns) columns.forEach(c => hiddenColumns.includes(c.field) ? c.hide = true : "");
        if (orderColumns) general.sortColumns(columns, orderColumns);

        setColumnDefs(columns);
    }, [onlyMyRequests]);

    const [columnDefs, setColumnDefs] = useState([]);

    const gridRef = useRef();

    const defaultColDef = useMemo(() => {
        return buildAccentInsensitiveColDef({
            resizable: true,
            editable: false,
            cellDataType: false,
        });
    }, []);

    const onFilterTextBoxChanged = useCallback(({target}) => {
        setAccentInsensitiveQuickFilter(gridRef, target.value);
    }, []);

    const onFilterChanged = () => {
        const filteredNodes = gridRef.current.api.getRenderedNodes();
        const filteredData = filteredNodes.map(node => node.data);
        countData(filteredData)
    }

    const onCellValueChanged = ({data, newValue}) => {
        saveData(data, onFilterChanged, queryClient)
    }

    const toggleColumn = (field) => {
        setColumnDefs((prevDefs) =>
            prevDefs.map((col) =>
                col.field === field ? {...col, hide: !col.hide} : col
            )
        );
    }

    useEffect(() => {
        const hiddenColumns = columnDefs.filter(c => c.hide).map(c => c.field);
        if (hiddenColumns.length > 0) localStorage.setItem("request-hidden-columns", JSON.stringify(hiddenColumns));
    }, [columnDefs]);

    const onColumnMoved = (params) => {
        const columns = params.api.getAllDisplayedColumns().map(col => col.colId);
        localStorage.setItem("request-order-columns", JSON.stringify(columns))
    }

    const exportLicensing = () => {
        const newArray = requests.map(data =>
            ({
                "Título": data.title,
                "Grupos": data.groups.map(g => g.name).join(", "),
                "Projetos": data.projects.map(p => p.name).join(", "),
                "Devs": data.devs.map(d => d.name).join(", "),
                "Status": data.status,
                "Solicitação": general.formatDate(data.date, "DD/MM/YYYY"),
                "Prazo de entrega": general.formatDate(data.deadline, "DD/MM/YYYY"),
                "GitLab": data.gitlab,
                "Solicitante": data.requester,
            })
        );

        general.exportCsv(newArray);
    }

    return (
        <TemplateDefault title="Solicitações">
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-4 lg:grid-cols-12">
                    <div className={"flex gap-4 col-span-2"}>
                        <Button target={"primary"}
                                col={"w-full"}
                                onClick={() => navigate("/requests/form")}>
                            <FaPlus/> Nova
                        </Button>
                        <div>
                            <Button
                                onClick={() => setShowTableProperties(!showTableProperties)}>
                                <FaEllipsis/>
                            </Button>

                            {
                                showTableProperties &&
                                <div
                                    className={"absolute z-[999] bg-white w-[200px] border border-gray-300 shadow-[0px_1px_5px_-3px_#000]"}>
                                    {
                                        columnDefs.filter(c => c.field !== "").map((column, index) => {
                                            return (
                                                <div key={index}
                                                     className={"flex justify-between items-center p-[5px] border-b border-gray-300 last:border-none"}>
                                                    <div
                                                        className={"text-[14px] font-bold"}>{column.headerName}</div>
                                                    <div
                                                        className={"text-[20px] cursor-pointer hover:text-gray-500"}
                                                        onClick={() => toggleColumn(column.field)}>
                                                        {column.hide && <FaRegEyeSlash/>}
                                                        {!column.hide && <FaRegEye/>}
                                                    </div>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            }
                        </div>
                    </div>
                    <SelectComponent
                        col={"col-span-2"}
                        instanceId="project"
                        label="Projeto"
                        options={projects ?? []}
                        value={filters.project}
                        onChange={(value) => handleSelect("project", value)}
                        name="project"
                    />
                    <SelectComponent
                        col={"col-span-2"}
                        instanceId="dev"
                        label="Dev"
                        options={devs ?? []}
                        value={filters.dev}
                        onChange={(value) => handleSelect("dev", value)}
                        name="dev"
                        isMulti
                    />
                    <SelectComponent
                        col={"col-span-3"}
                        instanceId="group"
                        label="Grupo"
                        options={groups ?? []}
                        value={filters.group}
                        onChange={(value) => handleSelect("group", value)}
                        name="group"
                        isMulti
                    />
                    <SelectComponent
                        col={"col-span-2"}
                        instanceId="status"
                        label="Status"
                        options={statusList}
                        value={filters.status}
                        onChange={(value) => handleSelect("status", value)}
                        name="status"
                        isMulti
                    />
                    <InputCheckbox id={"roadmap"} name={"roadmap"} label={"Roadmap"} col="col-span-12 lg:col-span-1"
                                   value={filters.roadmap}
                                   onChange={e => handleRoadmap(e)}
                    />

                    <SelectComponent
                        col={"col-span-3"}
                        instanceId="responsible"
                        label="Atendente"
                        options={responsibles ?? []}
                        value={filters.responsible}
                        onChange={(value) => handleSelect("responsible", value)}
                        name="responsible"
                    />

                    <div className={"flex col-span-9 gap-4"}>
                        <InputForm id="search" col="w-full" label="Pesquisar" type="search"
                                   onChange={onFilterTextBoxChanged}/>

                        <Button target="success" onClick={exportLicensing}>
                            <FaFileExcel />
                        </Button>
                    </div>

                </div>

                <div className={"ag-theme-quartz grid mt-2 ag-requests"}  style={{height: "calc(93vh - 182px)"}}>
                    <AgGridReact
                        ref={gridRef}
                        rowHeight={32}
                        rowData={requests}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        gridOptions={{localeText: LocalizationAgGrid}}
                        onFilterChanged={onFilterChanged}
                        // domLayout='autoHeight'
                        stopEditingWhenCellsLoseFocus={true}
                        singleClickEdit={true}
                        onCellValueChanged={onCellValueChanged}
                        onColumnMoved={onColumnMoved}
                    />
                </div>

                <div className="flex flex-col lg:grid grid-cols-1 gap-x-2 gap-y-4 lg:grid-cols-12 mt-2">
                    <div className="col-span-3"></div>
                    <div className="well-status well-status-open col-span-1">Doing: <b>{counters?.doing ?? 0}</b></div>
                    <div className="well-status well-status-travado col-span-1">Travado: <b>{counters?.travado ?? 0}</b>
                    </div>
                    <div className="well-status well danger col-span-1">Pendentes: <b>{counters?.pendente ?? 0}</b>
                    </div>
                    <div className="well-status well-status-close col-span-1">Backlog: <b>{counters?.backlog ?? 0}</b>
                    </div>
                    <div
                        className="well-status well-status-concluded col-span-1">Concluído: <b>{counters?.concluido ?? 0}</b>
                    </div>
                    <div className="well-status well warning col-span-1">Negados: <b>{counters?.negado ?? 0}</b>
                    </div>
                    <div className="well-status well-status-total col-span-1">Total: <b>{counters?.total ?? 0}</b>
                    </div>
                </div>

            </Card>
        </TemplateDefault>
    );
};

export default Requests;
